import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';

interface UrgencySectionProps {
  headline: string;
  pathOne: {
    title: string;
    description: string;
  };
  pathTwo: {
    title: string;
    description: string;
  };
  statement: string;
  capacityConstraint: string;
  ctaText?: string;
  ctaHref?: string;
}

// Icon components
const RocketIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.59 14.37a6 6 0 01-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 006.16-12.12A14.98 14.98 0 009.631 8.41m5.96 5.96a14.926 14.926 0 01-5.841 2.58m-.119-8.54a6 6 0 00-7.381 5.84h4.8m2.581-5.84a14.927 14.927 0 00-2.58 5.84m2.699 2.7c-.103.021-.207.041-.311.06a15.09 15.09 0 01-2.448-2.448 14.9 14.9 0 01.06-.312m-2.24 2.39a4.493 4.493 0 00-1.757 4.306 4.493 4.493 0 004.306-1.758M16.5 9a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
  </svg>
);

const ClockIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const CheckIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

const AlertIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
  </svg>
);

export const UrgencySection: React.FC<UrgencySectionProps> = ({
  headline,
  pathOne,
  pathTwo,
  statement,
  capacityConstraint,
  ctaText,
  ctaHref,
}) => {
  const sectionRef = useRef<HTMLElement>(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.2 });

  // Parse description into bullet points for better display
  const pathOnePoints = [
    "Book a call this week",
    "45-minute opportunity assessment",
    "Production AI in 6 weeks",
    "Measurable ROI within 90 days",
  ];

  const pathTwoPoints = [
    "Competitors deploy first",
    "Talent market tightens",
    "Technology shifts",
    "2-3x higher implementation costs",
  ];

  return (
    <section ref={sectionRef} className="relative py-16 sm:py-24 md:py-32 bg-[#0a0a0a] overflow-hidden">
      {/* Subtle gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-zinc-900/30 via-transparent to-zinc-900/30" />

      {/* Grid pattern */}
      <div className="absolute inset-0 opacity-[0.02]">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)`,
            backgroundSize: '48px 48px',
          }}
        />
      </div>

      {/* Top border */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-zinc-800 to-transparent" />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-zinc-800/50 border border-zinc-700/30 mb-6"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-zinc-500"></span>
            <span className="text-zinc-400 text-sm">Decision Point</span>
          </motion.div>

          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white">
            {headline}
          </h2>
        </motion.div>

        {/* Two paths comparison */}
        <div className="grid lg:grid-cols-2 gap-6 mb-16">
          {/* Path One - Recommended */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative group"
          >
            {/* Recommended badge */}
            <div className="absolute -top-3 left-6 z-10">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20">
                <CheckIcon className="w-3.5 h-3.5 text-emerald-400" />
                <span className="text-emerald-400 text-xs font-medium">Recommended</span>
              </div>
            </div>

            <div className="relative h-full bg-zinc-900/50 backdrop-blur-sm rounded-2xl border border-emerald-500/20 p-8 hover:border-emerald-500/30 transition-all duration-300">
              {/* Subtle glow */}
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-emerald-500/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

              <div className="relative">
                {/* Header */}
                <div className="flex items-start gap-4 mb-6">
                  <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                    <RocketIcon className="w-6 h-6 text-emerald-400" />
                  </div>
                  <div>
                    <span className="text-emerald-400 text-xs font-medium uppercase tracking-wider">Path 01</span>
                    <h3 className="text-xl font-bold text-white mt-1">{pathOne.title}</h3>
                  </div>
                </div>

                {/* Description */}
                <p className="text-zinc-400 leading-relaxed mb-6">
                  {pathOne.description}
                </p>

                {/* Key outcomes */}
                <div className="space-y-3 pt-4 border-t border-zinc-800/50">
                  <span className="text-zinc-500 text-xs font-medium uppercase tracking-wider">Key Outcomes</span>
                  <div className="grid grid-cols-2 gap-2">
                    {pathOnePoints.map((point, index) => (
                      <motion.div
                        key={point}
                        initial={{ opacity: 0, x: -10 }}
                        animate={isInView ? { opacity: 1, x: 0 } : {}}
                        transition={{ duration: 0.4, delay: 0.4 + index * 0.1 }}
                        className="flex items-center gap-2"
                      >
                        <div className="flex-shrink-0 w-4 h-4 rounded-full bg-emerald-500/10 flex items-center justify-center">
                          <CheckIcon className="w-2.5 h-2.5 text-emerald-400" />
                        </div>
                        <span className="text-zinc-300 text-sm">{point}</span>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Path Two - Warning */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="relative group"
          >
            <div className="relative h-full bg-zinc-900/50 backdrop-blur-sm rounded-2xl border border-zinc-800/50 p-8 hover:border-zinc-700/50 transition-all duration-300">
              <div className="relative">
                {/* Header */}
                <div className="flex items-start gap-4 mb-6">
                  <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-zinc-800/50 border border-zinc-700/30 flex items-center justify-center">
                    <ClockIcon className="w-6 h-6 text-zinc-500" />
                  </div>
                  <div>
                    <span className="text-zinc-500 text-xs font-medium uppercase tracking-wider">Path 02</span>
                    <h3 className="text-xl font-bold text-white mt-1">{pathTwo.title}</h3>
                  </div>
                </div>

                {/* Description */}
                <p className="text-zinc-400 leading-relaxed mb-6">
                  {pathTwo.description}
                </p>

                {/* Risk factors */}
                <div className="space-y-3 pt-4 border-t border-zinc-800/50">
                  <span className="text-zinc-500 text-xs font-medium uppercase tracking-wider">Risk Factors</span>
                  <div className="grid grid-cols-2 gap-2">
                    {pathTwoPoints.map((point, index) => (
                      <motion.div
                        key={point}
                        initial={{ opacity: 0, x: -10 }}
                        animate={isInView ? { opacity: 1, x: 0 } : {}}
                        transition={{ duration: 0.4, delay: 0.5 + index * 0.1 }}
                        className="flex items-center gap-2"
                      >
                        <div className="flex-shrink-0 w-4 h-4 rounded-full bg-amber-500/10 flex items-center justify-center">
                          <AlertIcon className="w-2.5 h-2.5 text-amber-500" />
                        </div>
                        <span className="text-zinc-400 text-sm">{point}</span>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Statement */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="text-center"
        >
          <div className="max-w-3xl mx-auto">
            {/* Quote marks */}
            <div className="flex justify-center mb-4">
              <svg className="w-8 h-8 text-zinc-700" viewBox="0 0 24 24" fill="currentColor">
                <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
              </svg>
            </div>

            <p className="text-xl sm:text-2xl text-zinc-200 font-medium mb-6 leading-relaxed">
              {statement}
            </p>

            <motion.p
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : {}}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="text-zinc-500 text-sm mb-10"
            >
              {capacityConstraint}
            </motion.p>

            {ctaText && ctaHref && (
              <motion.a
                href={ctaHref}
                initial={{ opacity: 0, y: 10 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.7 }}
                className="group inline-flex items-center gap-3 px-8 py-4 bg-white text-zinc-900 font-semibold rounded-xl transition-all duration-300 hover:shadow-xl hover:shadow-white/10"
                whileHover={{ y: -2, scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {ctaText}
                <svg className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </motion.a>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default UrgencySection;
